import { bind, classNames, Component, h, signal, tag } from "omi";
import includes from "lodash-es/includes";
import isEmpty from "lodash-es/isEmpty";

import { closeModal, IconName, NWIcon, NWProBadge, openProModal } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

type DropdownItemBase = {
  text: string;
  icon?: IconName;
  iconStyle?: string;
  isActive?: () => boolean;
  isProOption?: boolean;
  subtext?: string;
  shouldShow?: () => boolean;
};

export type DropdownItemLink = {
  type: "link";
  href: string;
} & DropdownItemBase;

export type DropdownItemButton = {
  type: "button";
  callback: (dropdownItem: DropdownItem) => void;
} & DropdownItemBase;

export type DropdownItemComingSoon = {
  type: "comingSoon";
  callback: (dropdownItem: DropdownItem) => void;
} & DropdownItemBase;

type Divider = {
  type: "divider";
};

export type DropdownItem = DropdownItemButton | DropdownItemLink | DropdownItemComingSoon | Divider;

type Props = {
  items: DropdownItem[];
  withBorder?: boolean;
  theme?: "transparent";
  width?: "fixed" | "fit" | "full";
  position?: "fixed" | "absolute";
  openByDefault?: boolean;
  openOrientation?: "top" | "bottom";
  customTrigger?: boolean;
  withAnimations?: boolean;
  limitHeight?: boolean;
  maxHeight?: string;
  withChevron?: boolean;
  fullWidthMenu?: boolean;
  isolateMouseEvents?: boolean;
  horizontalOrientation?: "left" | "right";
};

@tag("nw-dropdown")
export class NWDropdown extends Component<Props> {
  static css = [tailwind];
  static propTypes = {
    type: String,
    icon: String,
    iconStyle: String,
    text: String,
    subtext: String,
    theme: String,
    width: String,
    callback: Function,
    withTrigger: Boolean,
    withAnimations: Boolean,
    withBorder: Boolean,
    openByDefault: Boolean,
    openOrientation: String,
    limitHeight: Boolean,
    position: String,
    isolateMouseEvents: Boolean,
    horizontalOrientation: String,
    alignWithTrigger: Boolean,
    fullWidthMenu: Boolean,
    maxHeight: String,
  };

  private isOpen = signal<boolean>(false);
  private isClosing = signal<boolean>(false);
  private highlightedIndex = signal(-1);

  install() {
    this.isOpen.value = this.props.openByDefault || false;
  }

  @bind
  private handleOpen(event: MouseEvent, shouldOpen: boolean): void {
    if (this.props.isolateMouseEvents) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (shouldOpen) {
      document.addEventListener("mousedown", this.withinBoundsListener);
      this.isClosing.value = false;
      this.isOpen.value = true;
    } else {
      document.removeEventListener("mousedown", this.withinBoundsListener);
      this.closeDropdown();
    }
  }

  @bind
  private withinBoundsListener(event: MouseEvent): void {
    if (!includes(event.composedPath(), this.rootElement)) {
      this.closeDropdown();
    }
  }

  @bind
  private closeDropdown(): void {
    this.isClosing.value = true;

    onAnimationFinish(60, () => {
      this.isOpen.value = false;
    });
  }

  @bind
  private handleItemClick(item: DropdownItem, event?: MouseEvent): void {
    if (event && this.props.isolateMouseEvents) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (item.type != "divider" && item.isProOption && !Store.user.isPro()) {
      closeModal();
      setTimeout(() => {
        openProModal();
      }, 350);
      return;
    }

    if (item.type === "comingSoon") {
      // Coming soon options do not close the dropdown
      item.callback(item);
      return;
    }

    this.isOpen.value = false;

    if (item.type === "button") {
      item.callback(item);
    }
  }

  @bind
  private handleKeyDown(event: KeyboardEvent) {
    if (!this.isOpen.value) {
      if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
        this.isOpen.value = true;
        event.preventDefault();
      }
      return;
    }

    switch (event.key) {
      case "ArrowUp":
        this.highlightedIndex.value =
          this.highlightedIndex.value > 0 ? this.highlightedIndex.value - 1 : this.props.items.length - 1;
        event.preventDefault();
        break;
      case "ArrowDown":
        this.highlightedIndex.value =
          this.highlightedIndex.value < this.props.items.length - 1 ? this.highlightedIndex.value + 1 : 0;
        event.preventDefault();
        break;
      case "Enter":
      case " ":
        if (this.highlightedIndex.value !== -1) {
          this.handleItemClick(this.props.items[this.highlightedIndex.value]);
        }
        event.preventDefault();
        break;
      case "Escape":
        this.closeDropdown();
        event.preventDefault();
        break;
    }
  }

  private isDivider(item: DropdownItem): item is Divider {
    return item.type === "divider";
  }

  render({
    withBorder = true,
    width = "fixed",
    openOrientation = "bottom",
    horizontalOrientation = "left",
    customTrigger = false,
    withAnimations = true,
    limitHeight = true,
    position = "fixed",
    withChevron = false,
    fullWidthMenu = false,
    maxHeight,
  }: Props) {
    const itemStyles =
      "text-secondary cursor-pointer select-none text-sm px-3 h-10 flex items-center hover:bg-gray-50 space-x-2";

    return (
      <div
        class={classNames("relative", { "transform-gpu": position === "fixed" && horizontalOrientation === "right" })}
      >
        {!customTrigger ? (
          <button
            id="trigger"
            class={classNames(
              "px-3 py-1.5 flex relative items-center rounded-lg transition-colors group hover:bg-gray-50 cursor-pointer border focus:shadow-xss",
              { "shadow-xss border-brand-300": this.isOpen.peek() },
              { "border-gray-200 bg-white": withBorder && !this.isOpen.peek() },
              {
                "border-transparent bg-transparent hover:border hover:border-gray-200":
                  !withBorder && !this.isOpen.peek(),
              },
              { "w-full": width === "full" },
            )}
            onClick={e => this.handleOpen(e, !this.isOpen.value)}
            onKeyDown={this.handleKeyDown}
            aria-haspopup="listbox"
            aria-expanded={this.isOpen.value}
            aria-labelledby="trigger"
          >
            <div
              class={classNames("text-lg text-primary text-nowrap text-ellipsis overflow-hidden", {
                "flex items-center justify-between flex-1": withChevron,
              })}
            >
              <slot></slot>
              {withChevron && (
                <div className="bg-white h-[80%] flex items-center justify-center transition-colors group-hover:bg-gray-50 absolute right-[2px]">
                  <NWIcon
                    name="chevron-down"
                    class={classNames("w-3 h-3 mx-3 text-tertiary  transition-all", {
                      "rotate-180": this.isOpen.value,
                    })}
                  />
                </div>
              )}
            </div>
          </button>
        ) : (
          <div onClick={e => this.handleOpen(e, !this.isOpen.value)}>
            <slot />
          </div>
        )}

        {this.isOpen.value && (
          <div>
            <div
              class={classNames(
                { absolute: position === "absolute", fixed: position === "fixed" },
                "border mt-1 py-1.5 flex items-center rounded-lg z-[60] bg-[#FFF] shadow-lg",
                withAnimations && {
                  "animate-dropdownEntry": !this.isClosing.value && openOrientation === "bottom",
                  "animate-dropdownExit": this.isClosing.value && openOrientation === "bottom",
                  "animate-dropdownEntryUp": !this.isClosing.value && openOrientation === "top",
                  "animate-dropdownExitUp": this.isClosing.value && openOrientation === "top",
                },
                {
                  "bottom-11": openOrientation === "top" && customTrigger,
                  "bottom-0": openOrientation === "top" && !customTrigger,
                },
                fullWidthMenu
                  ? { "w-full max-w-md": true }
                  : { "w-[280px] max-w-xs": width != "fit", "w-fit max-w-xs": width === "fit" },
                { "right-0": horizontalOrientation === "right" },
              )}
              onClick={e => {
                if (this.props.isolateMouseEvents) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              <ul
                class={classNames(
                  "w-full overflow-y-auto",
                  {
                    "max-h-60": limitHeight && isEmpty(maxHeight),
                  },
                  !isEmpty(maxHeight) ? [maxHeight] : {},
                )}
                tabIndex={-1}
                role="listbox"
              >
                {this.props.items.map((item, idx) => {
                  if (this.isDivider(item)) return <li class="border-b bg-gray-200" />;

                  if (item.shouldShow && !item.shouldShow()) {
                    return null;
                  }

                  const content = (
                    <span class="flex items-center gap-2">
                      {item.icon && (
                        <div class={classNames("text-quaternary", item.iconStyle)}>
                          <NWIcon name={item.icon} width={16} height={16} />
                        </div>
                      )}
                      <span class="text-nowrap" unsafeHTML={{ html: item.text }}></span>
                    </span>
                  );

                  if (item.type === "link") {
                    return (
                      <a
                        class={itemStyles}
                        tabIndex="0"
                        href={item.href}
                        target="_blank"
                        onClick={e => {
                          if (this.props.isolateMouseEvents) {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(item.href, "_blank");
                          }
                        }}
                      >
                        {content}
                      </a>
                    );
                  }

                  if (item.type === "comingSoon") {
                    return (
                      <li
                        onClick={e => this.handleItemClick(item, e)}
                        tabIndex="0"
                        class={classNames(itemStyles, "justify-between !cursor-not-allowed")}
                        role="option"
                        onMouseEnter={() => (this.highlightedIndex.value = idx)}
                        onMouseLeave={() => (this.highlightedIndex.value = -1)}
                      >
                        <div class="flex items-center gap-2">{content}</div>
                        <span class="rounded-md border px-1 py-0.5 text-xs text-quaternary">Coming soon</span>
                      </li>
                    );
                  }

                  return (
                    <li
                      onClick={e => this.handleItemClick(item, e)}
                      tabIndex="0"
                      class={classNames(
                        itemStyles,
                        { "justify-between": item.isProOption },
                        { "bg-gray-50": idx === this.highlightedIndex.value },
                        { "justify-between": !!item.subtext },
                      )}
                      role="option"
                      onMouseEnter={() => (this.highlightedIndex.value = idx)}
                      onMouseLeave={() => (this.highlightedIndex.value = -1)}
                    >
                      {content}
                      {item.isActive && item.isActive() && <NWIcon name="tick" />}
                      {item.isProOption && <NWProBadge location="dropdown-item" analyticsData={{ name: item.text }} />}
                      {item.subtext && <span class="float-right text-xs text-quinary pl-2">{item.subtext}</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  }
}

function onAnimationFinish(animationDuration: number, callback: () => void): void {
  const start = performance.now();
  const animate = (time: number) => {
    if (time - start >= animationDuration) {
      callback();
    } else {
      requestAnimationFrame(animate);
    }
  };
  requestAnimationFrame(animate);
}
